/**
 * BucketsTab — User-curated selections of clips.
 *
 * Shows bucket cards with their member clips. Users can create new
 * buckets by selecting clips, add clips to existing buckets, rename,
 * delete buckets and remove clips. Clips can be drag-and-drop
 * reordered within each bucket.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, FolderOpen, Film, X, Trash2, Edit2, Check, Package, Sparkles, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDndSensors } from '../../hooks/useDndSensors'
import apiClient from '../../services/api'
import MediaPreviewModal from '../MediaPreviewModal'
import { useMediaPreview } from '../../hooks/useMediaPreview'
import { apiErrorDetail } from '../../utils/dataValidator'

interface MomentBrief {
  id: number
  scene_index: number
  title: string | null
  visual_description: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  asset_filename: string | null
}

interface BucketMoment {
  id: number
  scene_index: number
  title: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  asset_filename: string | null
  asset_id: number | null
  asset_file_type: string | null
}

interface BucketItem {
  id: number
  project_id: string
  name: string
  description: string | null
  moments_count: number
  moments: BucketMoment[]
  created_time: string | null
}

interface BucketsTabProps {
  projectIds: string[]
  onGenerateDraft?: (scope: { scope_type: string; scope_ref_id: number; label: string; project_id: string }) => void
}

// ---------------------------------------------------------------------------
// Sortable clip card
// ---------------------------------------------------------------------------

function SortableBucketClip({
  moment,
  onRemove,
  onPreview,
}: {
  moment: BucketMoment
  onRemove: () => void
  onPreview: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: moment.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex-shrink-0 w-40 bg-black/25 border border-white/10 rounded-lg overflow-hidden group relative"
    >
      <div
        className="aspect-video bg-black/40 relative cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        {moment.thumbnail_url ? (
          <img
            src={moment.thumbnail_url}
            alt={moment.title || 'Clip'}
            className="w-full h-full object-cover pointer-events-none"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
          </div>
        )}
        <button
          onClick={e => { e.stopPropagation(); onPreview() }}
          onPointerDown={e => e.stopPropagation()}
          className="absolute top-1 left-1 bg-black/70 text-white/80 hover:text-white p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Preview"
        >
          <Eye className="w-3 h-3" />
        </button>
        <button
          onClick={e => { e.stopPropagation(); onRemove() }}
          onPointerDown={e => e.stopPropagation()}
          className="absolute top-1 right-1 bg-black/70 text-[var(--rq-text-soft)] hover:text-red-400 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Remove from bucket"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="p-1.5">
        <p className="text-xs text-[var(--rq-text-soft)] truncate">
          {moment.title || moment.asset_filename || 'Untitled'}
        </p>
      </div>
    </div>
  )
}

function BucketClipOverlay({ moment }: { moment: BucketMoment }) {
  return (
    <div className="flex-shrink-0 w-40 bg-black/25 border border-white/10 rounded-lg overflow-hidden ring-2 ring-[var(--btn)] rotate-2 shadow-xl">
      <div className="aspect-video bg-black/40">
        {moment.thumbnail_url ? (
          <img
            src={moment.thumbnail_url}
            alt={moment.title || 'Clip'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
          </div>
        )}
      </div>
      <div className="p-1.5">
        <p className="text-xs text-[var(--rq-text-soft)] truncate">
          {moment.title || moment.asset_filename || 'Untitled'}
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function BucketsTab({ projectIds, onGenerateDraft }: BucketsTabProps) {
  const [buckets, setBuckets] = useState<BucketItem[]>([])
  const [moments, setMoments] = useState<MomentBrief[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create bucket modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [selectedMomentIds, setSelectedMomentIds] = useState<Set<number>>(new Set())
  const [creating, setCreating] = useState(false)

  // Add clips to existing bucket modal
  const [addToBucketId, setAddToBucketId] = useState<number | null>(null)
  const [addSelectedIds, setAddSelectedIds] = useState<Set<number>>(new Set())
  const [addingClips, setAddingClips] = useState(false)

  // Inline edit
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  // DnD state
  const [activeClipId, setActiveClipId] = useState<number | null>(null)
  const bucketsSnapshot = useRef<BucketItem[]>([])
  const sensors = useDndSensors()

  const { preview, closePreview, openFromMoment } = useMediaPreview()

  const activeMoment = activeClipId
    ? buckets.flatMap(b => b.moments).find(m => m.id === activeClipId) ?? null
    : null

  const fetchData = useCallback(async () => {
    if (projectIds.length === 0) return
    try {
      setLoading(true)
      setError(null)
      const projectsParam = projectIds.join(',')
      const [buckRes, momRes] = await Promise.all([
        apiClient.get('/buckets/', { params: { project_ids: projectsParam } }),
        apiClient.get('/moments/', { params: { project_ids: projectsParam, limit: 200 } }),
      ])
      setBuckets(buckRes.data.buckets)
      setMoments(momRes.data.moments)
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load buckets'))
    } finally {
      setLoading(false)
    }
  }, [projectIds])

  useEffect(() => { fetchData() }, [fetchData])

  // --- Create bucket ---

  const openCreateModal = () => {
    if (projectIds.length !== 1) {
      toast.error('Select exactly one workspace to create a bucket')
      return
    }
    setNewName('')
    setNewDesc('')
    setSelectedMomentIds(new Set())
    setShowCreateModal(true)
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setNewName('')
    setNewDesc('')
    setSelectedMomentIds(new Set())
  }

  const toggleMomentForCreate = (id: number) => {
    setSelectedMomentIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const createBucket = async () => {
    if (projectIds.length === 0 || !newName.trim()) return
    try {
      setCreating(true)
      await apiClient.post(`/buckets/?project_id=${projectIds[0]}`, {
        name: newName.trim(),
        description: newDesc.trim() || null,
        moment_ids: Array.from(selectedMomentIds),
      })
      const clipCount = selectedMomentIds.size
      toast.success(
        clipCount > 0
          ? `Created bucket "${newName.trim()}" with ${clipCount} clip${clipCount !== 1 ? 's' : ''}`
          : `Bucket "${newName.trim()}" created`
      )
      closeCreateModal()
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to create bucket'))
    } finally {
      setCreating(false)
    }
  }

  // --- Add clips to existing bucket ---

  const openAddClipsModal = (bucketId: number) => {
    setAddToBucketId(bucketId)
    setAddSelectedIds(new Set())
    setAddingClips(false)
  }

  const closeAddClipsModal = () => {
    setAddToBucketId(null)
    setAddSelectedIds(new Set())
  }

  const toggleMomentForAdd = (id: number) => {
    setAddSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const addClipsToBucket = async () => {
    if (addToBucketId === null || addSelectedIds.size === 0) return
    try {
      setAddingClips(true)
      await apiClient.post(`/buckets/${addToBucketId}/moments`, {
        moment_ids: Array.from(addSelectedIds),
      })
      toast.success(`Added ${addSelectedIds.size} clip${addSelectedIds.size !== 1 ? 's' : ''} to bucket`)
      closeAddClipsModal()
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to add clips'))
    } finally {
      setAddingClips(false)
    }
  }

  // --- Other actions ---

  const deleteBucket = async (id: number) => {
    const bucket = buckets.find(b => b.id === id)
    if (!confirm(`Delete bucket "${bucket?.name || 'Untitled'}"${bucket && bucket.moments_count > 0 ? ` and its ${bucket.moments_count} clip${bucket.moments_count !== 1 ? 's' : ''}` : ''}? This cannot be undone.`)) return
    try {
      await apiClient.delete(`/buckets/${id}`)
      toast.success('Bucket deleted')
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete bucket'))
    }
  }

  const saveEdit = async (id: number) => {
    if (!editName.trim()) return
    try {
      await apiClient.patch(`/buckets/${id}`, { name: editName.trim() })
      setEditingId(null)
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to update bucket'))
    }
  }

  const removeMoment = async (bucketId: number, momentId: number) => {
    try {
      await apiClient.delete(`/buckets/${bucketId}/moments/${momentId}`)
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to remove clip'))
    }
  }

  // --- DnD handlers ---

  const handleDragStart = (event: DragStartEvent) => {
    bucketsSnapshot.current = buckets.map(b => ({ ...b, moments: [...b.moments] }))
    setActiveClipId(Number(event.active.id))
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveClipId(null)
    if (!over || active.id === over.id) return

    const activeContainer = active.data.current?.sortable?.containerId
    const overContainer = over.data.current?.sortable?.containerId
    if (!activeContainer || !overContainer || activeContainer !== overContainer) return

    const bucketId = Number(String(activeContainer).replace('bucket-', ''))
    const bucket = buckets.find(b => b.id === bucketId)
    if (!bucket) return

    const oldIndex = bucket.moments.findIndex(m => m.id === Number(active.id))
    const newIndex = bucket.moments.findIndex(m => m.id === Number(over.id))
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

    const reordered = arrayMove(bucket.moments, oldIndex, newIndex)
    setBuckets(prev => prev.map(b =>
      b.id === bucketId ? { ...b, moments: reordered } : b
    ))

    try {
      await apiClient.patch(`/buckets/${bucketId}/reorder`, {
        moment_ids: reordered.map(m => m.id),
      })
    } catch {
      toast.error('Failed to reorder clips')
      setBuckets(bucketsSnapshot.current)
    }
  }

  const handleDragCancel = () => {
    setBuckets(bucketsSnapshot.current)
    setActiveClipId(null)
  }

  // --- Computed ---

  const addToBucket = addToBucketId !== null ? buckets.find(b => b.id === addToBucketId) : null
  const existingMomentIds = addToBucket ? new Set(addToBucket.moments.map(m => m.id)) : new Set<number>()
  const availableForAdd = moments.filter(m => !existingMomentIds.has(m.id))

  // --- Render ---

  if (projectIds.length === 0) {
    return (
      <div className="text-center py-16">
        <FolderOpen className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">Select a workspace</h2>
        <p className="text-[var(--rq-text-soft)]">Choose a workspace above to manage buckets</p>
      </div>
    )
  }

  if (loading) return <div className="text-center py-16 text-[var(--rq-text-soft)]">Loading buckets...</div>
  if (error) return <div className="text-center py-16 text-red-400">{error}</div>

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[var(--rq-text-soft)]">
          {buckets.length} bucket{buckets.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={openCreateModal}
          disabled={projectIds.length !== 1}
          className="rq-btn-primary px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          title={projectIds.length !== 1 ? 'Select exactly one workspace' : undefined}
        >
          <Plus className="w-4 h-4" />
          New Bucket
        </button>
      </div>

      {/* Empty state */}
      {buckets.length === 0 && (
        <div className="text-center py-16">
          <FolderOpen className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No buckets yet</h2>
          <p className="text-[var(--rq-text-soft)] max-w-md mx-auto">
            Create a bucket and select clips to add to it.
          </p>
        </div>
      )}

      {/* Bucket cards with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
      <div className="space-y-4">
        {buckets.map(bucket => (
          <div
            key={bucket.id}
            className="rq-card overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              {editingId === bucket.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="rq-input-dark text-sm flex-1 px-2 py-1"
                    onKeyDown={e => e.key === 'Enter' && saveEdit(bucket.id)}
                  />
                  <button onClick={() => saveEdit(bucket.id)} className="text-green-400 hover:text-green-300 p-1">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <h3 className="text-[var(--rq-text)] font-medium text-sm">{bucket.name}</h3>
                    {bucket.description && (
                      <p className="text-xs text-[var(--rq-text-soft)] mt-0.5">{bucket.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[var(--rq-text-soft)] mr-2">
                      {bucket.moments_count} clip{bucket.moments_count !== 1 ? 's' : ''}
                    </span>
                    {onGenerateDraft && (
                      <button
                        onClick={() => onGenerateDraft({ scope_type: 'bucket', scope_ref_id: bucket.id, label: `Bucket: ${bucket.name}`, project_id: bucket.project_id || projectIds[0] })}
                        className="text-[var(--rq-text-soft)] hover:text-[var(--brand-light)] p-1 transition-colors"
                        title="Generate AI Draft"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => openAddClipsModal(bucket.id)}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--brand-light)] p-1 transition-colors"
                      title="Add clips"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => { setEditingId(bucket.id); setEditName(bucket.name) }}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                      title="Rename"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteBucket(bucket.id)}
                      className="text-[var(--rq-text-soft)] hover:text-red-400 p-1 transition-colors"
                      title="Delete bucket"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Clips row */}
            {bucket.moments.length > 0 ? (
              <SortableContext
                id={`bucket-${bucket.id}`}
                items={bucket.moments.map(m => m.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="p-3 flex gap-3 overflow-x-auto">
                  {bucket.moments.map(moment => (
                    <SortableBucketClip
                      key={moment.id}
                      moment={moment}
                      onRemove={() => removeMoment(bucket.id, moment.id)}
                      onPreview={() => openFromMoment(moment)}
                    />
                  ))}
                </div>
              </SortableContext>
            ) : (
              <div className="p-4 text-center text-xs text-[var(--rq-text-soft)]">
                No clips yet — click <Plus className="w-3 h-3 inline" /> above to add clips.
              </div>
            )}
          </div>
        ))}
      </div>

      <DragOverlay>
        {activeMoment ? <BucketClipOverlay moment={activeMoment} /> : null}
      </DragOverlay>
      </DndContext>

      {/* ---- Create New Bucket Modal ---- */}
      {showCreateModal && (
        <div className="rq-modal-overlay">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeCreateModal}
          />

          <div className="relative w-full max-w-lg mx-4 rq-modal-card max-h-[85vh] flex flex-col">
            <Package className="w-10 h-10 text-[var(--rq-text-soft)] mb-4" />
            <h2 className="text-xl font-bold text-[var(--rq-text)] mb-5">Create New Bucket</h2>

            {/* Clip count banner */}
            {selectedMomentIds.size > 0 && (
              <div className="flex items-center gap-3 bg-emerald-900/30 border border-emerald-700/40 rounded-xl px-4 py-3 mb-5">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-emerald-200">
                  {selectedMomentIds.size} clip{selectedMomentIds.size !== 1 ? 's' : ''} will be added to this bucket
                </span>
              </div>
            )}

            {/* Bucket Name */}
            <label className="block text-sm font-medium text-[var(--rq-text-soft)] mb-2">Bucket Name</label>
            <div className="relative mb-4">
              <input
                autoFocus
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Outdoor talking head shots"
                className="w-full rq-input-dark rounded-xl px-4 py-3 pr-10 text-sm"
                onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) createBucket() }}
              />
              {newName && (
                <button
                  onClick={() => setNewName('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Description */}
            <label className="block text-sm font-medium text-[var(--rq-text-soft)] mb-2">Description (Optional)</label>
            <textarea
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              placeholder="Add a description for this bucket"
              rows={2}
              className="w-full rq-input-dark rounded-xl px-4 py-3 text-sm resize-none mb-4"
            />

            {/* Clip picker */}
            <label className="block text-sm font-medium text-[var(--rq-text-soft)] mb-2">
              Select Clips {moments.length > 0 && <span className="text-[var(--rq-text-soft)] font-normal">({moments.length} available)</span>}
            </label>
            <div className="flex-1 min-h-0 overflow-y-auto border border-white/10 rounded-xl bg-black/25 mb-5">
              {moments.length === 0 ? (
                <div className="text-center py-8 text-sm text-[var(--rq-text-soft)]">
                  No clips available — upload videos and analyze them first.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 p-3">
                  {moments.map(m => {
                    const isSelected = selectedMomentIds.has(m.id)
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggleMomentForCreate(m.id)}
                        className={`relative rounded-lg overflow-hidden border transition-all text-left ${
                          isSelected
                            ? 'border-[var(--btn)] ring-1 ring-[var(--btn)]'
                            : 'border-white/10 hover:border-white/25'
                        }`}
                      >
                        <div className="aspect-video bg-black/35">
                          {m.thumbnail_url ? (
                            <img src={m.thumbnail_url} alt={m.title || 'Clip'} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-5 h-5 text-[var(--rq-text-soft)]" />
                            </div>
                          )}
                          <div className={`absolute top-1 left-1 w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected
                              ? 'bg-[var(--btn)] border-[var(--btn)]'
                              : 'border-white/40 bg-black/30'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <div className="p-1">
                          <p className="text-[10px] text-[var(--rq-text-soft)] truncate">
                            {m.title || m.asset_filename || 'Untitled'}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeCreateModal}
                className="rq-btn-ghost px-5 py-2.5 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={createBucket}
                disabled={!newName.trim() || creating}
                className="rq-btn-primary px-5 py-2.5 disabled:opacity-40 text-sm"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Add Clips to Existing Bucket Modal ---- */}
      {addToBucketId !== null && addToBucket && (
        <div className="rq-modal-overlay">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeAddClipsModal}
          />

          <div className="relative w-full max-w-lg mx-4 rq-modal-card max-h-[85vh] flex flex-col">
            <FolderOpen className="w-10 h-10 text-[var(--rq-text-soft)] mb-4" />
            <h2 className="text-xl font-bold text-[var(--rq-text)] mb-1">Add Clips</h2>
            <p className="text-sm text-[var(--rq-text-soft)] mb-5">
              Select clips to add to <span className="text-[var(--rq-text)] font-medium">{addToBucket.name}</span>
            </p>

            {/* Selected count banner */}
            {addSelectedIds.size > 0 && (
              <div className="flex items-center gap-3 bg-emerald-900/30 border border-emerald-700/40 rounded-xl px-4 py-3 mb-5">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-emerald-200">
                  {addSelectedIds.size} clip{addSelectedIds.size !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}

            {/* Clip picker */}
            <div className="flex-1 min-h-0 overflow-y-auto border border-white/10 rounded-xl bg-black/25 mb-5">
              {availableForAdd.length === 0 ? (
                <div className="text-center py-8 text-sm text-[var(--rq-text-soft)]">
                  All clips are already in this bucket.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 p-3">
                  {availableForAdd.map(m => {
                    const isSelected = addSelectedIds.has(m.id)
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggleMomentForAdd(m.id)}
                        className={`relative rounded-lg overflow-hidden border transition-all text-left ${
                          isSelected
                            ? 'border-[var(--btn)] ring-1 ring-[var(--btn)]'
                            : 'border-white/10 hover:border-white/25'
                        }`}
                      >
                        <div className="aspect-video bg-black/35">
                          {m.thumbnail_url ? (
                            <img src={m.thumbnail_url} alt={m.title || 'Clip'} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-5 h-5 text-[var(--rq-text-soft)]" />
                            </div>
                          )}
                          <div className={`absolute top-1 left-1 w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected
                              ? 'bg-[var(--btn)] border-[var(--btn)]'
                              : 'border-white/40 bg-black/30'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <div className="p-1">
                          <p className="text-[10px] text-[var(--rq-text-soft)] truncate">
                            {m.title || m.asset_filename || 'Untitled'}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeAddClipsModal}
                className="rq-btn-ghost px-5 py-2.5 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={addClipsToBucket}
                disabled={addSelectedIds.size === 0 || addingClips}
                className="rq-btn-primary px-5 py-2.5 disabled:opacity-40 text-sm"
              >
                {addingClips ? 'Adding...' : `Add ${addSelectedIds.size > 0 ? addSelectedIds.size + ' ' : ''}Clip${addSelectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <MediaPreviewModal preview={preview} onClose={closePreview} />
    </div>
  )
}
